import { getVariableValue } from "@tamagui/core";
import { cloneElement, createElement, isValidElement } from "react";
import { getTokenValue, Token, Variable } from "tamagui";

import {
  ComponentSizeVariantProps,
  EntriesToObject,
  EntriesType,
  NegativeSpaceToken,
  ThemeableIcon,
  ThemeableIconProps,
} from "./types";

const getColorSchemeValues = <
  ColorObjType extends { [key: string]: { [key in SchemeType]: ValueType } },
  ValueType extends string,
  SchemeType extends "dark" | "light",
>(
  colorValues: ColorObjType,
  scheme: SchemeType,
): {
  [key in keyof ColorObjType]: ValueType;
} =>
  objectFromEntries(
    objectKeys(colorValues).map((name) => [name, colorValues[name][scheme]]),
  ) as any;

const getComputedSpaceValue = (value: any): number => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.startsWith("$")) {
    return getTokenValue(value as Token, "space");
  }
  return 0;
};

const getInvertedSpaceValue = (value: any): NegativeSpaceToken | number => {
  if (typeof value === "number") {
    return -value;
  }
  if (typeof value === "string" && value.startsWith("$")) {
    return getTokenValue(value as Token, "space");
  }
  return 0;
};

/**
 * Clones icon components so that they have a custom size and color.
 *
 * Based on Tamagui's `useGetThemedIcon` but allows us to pass a size token instead of a font size token.
 */
const getThemedIcon = (icon: ThemeableIcon, props: ThemeableIconProps) => {
  if (!icon) {
    return icon;
  }

  if (isValidElement(icon)) {
    return cloneElement(icon, {
      ...props,
      // @ts-expect-error
      ...icon.props,
    });
  }

  // @ts-expect-error
  return createElement(icon, props);
};

/**
 * Returns an object where only one of the size variant props is `true`, so that variants don't override each other.
 * Defaults to `default` if none are `true`.
 */
const normalizeComponentSizeVariantProps = ({
  large,
  small,
}: ComponentSizeVariantProps): ComponentSizeVariantProps => ({
  default: !large && !small,
  large: large ?? false,
  small: small ?? false,
});

/**
 * Returns the name of the size variant prop that is `true`.
 * Defaults to `default` if none are `true`.
 */
const getComponentSizeVariantName = ({
  large,
  small,
}: ComponentSizeVariantProps) =>
  (large
    ? "large"
    : small
      ? "small"
      : "default") as keyof ComponentSizeVariantProps;

// All functions below were taken from the Tamagui source code
// https://github.com/tamagui/tamagui/blob/master/code/ooo/helpers/typeHelpers.ts

function postfixObjKeys<A extends { [key: string]: string }, B extends string>(
  obj: A,
  postfix: B,
): {
  [Key in `${keyof A extends string ? keyof A : never}${B}`]: string;
} {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [`${k}${postfix}`, v]),
  ) as any;
}

function objectFromEntries<ARR_T extends EntriesType>(
  arr: ARR_T,
): EntriesToObject<ARR_T> {
  return Object.fromEntries(arr) as EntriesToObject<ARR_T>;
}

function objectKeys<O extends object>(obj: O) {
  return Object.keys(obj) as Array<keyof O>;
}

// Color utilities taken from the Tamagui source code
// https://github.com/tamagui/tamagui/blob/master/code/core/create-themes/src/colorUtils.tsx

const hexToRGBA = (hex: string, alpha = 1) => {
  if (!isValidHex(hex)) {
    throw new Error("Invalid HEX");
  }
  const chunkSize = Math.floor((hex.length - 1) / 3);
  const hexArr = getChunksFromString(hex.slice(1), chunkSize);
  const [r, g, b, a] = hexArr?.map(convertHexUnitTo256) ?? [];
  return `rgba(${r}, ${g}, ${b}, ${getAlphaFloat(a, alpha)})`;
};

const setColorAlpha = (colorIn: string | Variable, alpha = 1) => {
  const color = `${getVariableValue(colorIn)}`;
  if (color.startsWith("hsl(")) {
    return color.replace("hsl", "hsla").replace(")", `, ${alpha})`);
  }
  if (color.startsWith("#")) {
    return hexToRGBA(color, alpha);
  }
  if (color.startsWith("rgb(")) {
    return color.replace("rgb", "rgba").replace(")", `, ${alpha})`);
  }
  return color;
};

const isValidHex = (hex: string) => /^#([A-Fa-f0-9]{3,4}){1,2}$/.test(hex);

const getChunksFromString = (st: string, chunkSize = 0) =>
  st.match(new RegExp(`.{${chunkSize}}`, "g"));

const convertHexUnitTo256 = (hexStr: string) =>
  Number.parseInt(hexStr.repeat(2 / hexStr.length), 16);

const getAlphaFloat = (a: any, alpha = 1) => {
  if (a !== undefined) {
    return a / 255;
  }
  if (typeof alpha !== "number" || alpha < 0 || alpha > 1) {
    return 1;
  }
  return alpha;
};

export {
  getColorSchemeValues,
  getComputedSpaceValue,
  getInvertedSpaceValue,
  getThemedIcon,
  normalizeComponentSizeVariantProps,
  getComponentSizeVariantName,
  postfixObjKeys,
  objectFromEntries,
  objectKeys,
  hexToRGBA,
  setColorAlpha,
};
